import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  laborMutations,
  materialMutations,
  workorderMutations,
  workorderQueries,
} from './workorders.queries';
import type { WorkorderFilter } from './workorders.api';
import type { Workorder } from '@certhon/models';
import { useCurrentUser } from '@/features/users/users.hooks';
import { getWorkorderLockReason } from './workorders.utils';

// ============================================================================
// Workorder Hooks
// ============================================================================

/**
 * Whether the current user may edit the given workorder, and why not.
 * While the workorder is still loading it is reported as locked without a
 * reason, so panels render read-only until they know better.
 */
export function useWorkorderLock(workorder: Workorder | undefined): {
  locked: boolean;
  reason: string | null;
} {
  const currentUser = useCurrentUser();
  if (!workorder) return { locked: true, reason: null };
  const reason = getWorkorderLockReason(workorder, currentUser?.recnum);
  return { locked: reason !== null, reason };
}

export function useWorkorders(filter?: WorkorderFilter) {
  return useQuery(workorderQueries.list(filter));
}

export function useWorkorder(recnum: number) {
  return useQuery(workorderQueries.detail(recnum));
}

export function useUpdateWorkorderMutation() {
  return useMutation(workorderMutations.update());
}

export function useCompleteWorkorderMutation(recnum: number) {
  return useMutation(workorderMutations.complete(recnum));
}

export function useSignWorkorderMutation(recnum: number) {
  return useMutation(workorderMutations.sign(recnum));
}

// ============================================================================
// Labor Hooks
// ============================================================================

export function useWorkorderLabor(workorderRecnum: number) {
  return useQuery(workorderQueries.labor(workorderRecnum));
}

export function useLaborEntry(recnum: number) {
  return useQuery(workorderQueries.laborDetail(recnum));
}

export function useCreateLaborMutation(workorderRecnum: number) {
  return useMutation(laborMutations.create(workorderRecnum));
}

export function useUpdateLaborMutation(workorderRecnum: number) {
  return useMutation(laborMutations.update(workorderRecnum));
}

export function useDeleteLaborMutation(workorderRecnum: number) {
  return useMutation(laborMutations.delete(workorderRecnum));
}

// ============================================================================
// Material Hooks
// ============================================================================

export function useWorkorderMaterials(workorderRecnum: number) {
  return useQuery(workorderQueries.materials(workorderRecnum));
}

export function useMaterialEntry(recnum: number) {
  return useQuery(workorderQueries.materialDetail(recnum));
}

export function useCreateMaterialMutation(workorderRecnum: number) {
  return useMutation(materialMutations.create(workorderRecnum));
}

export function useUpdateMaterialMutation(workorderRecnum: number) {
  return useMutation(materialMutations.update(workorderRecnum));
}

export function useDeleteMaterialMutation(workorderRecnum: number) {
  return useMutation(materialMutations.delete(workorderRecnum));
}
